// ─── Login Page (ocn-S281-P0) ────────────────────────────────────
// Multi-option subscriber login:
//   - Email + password (sign-in + sign-up)
//   - Email magic-link (passwordless via Mailgun)
//   - Continue with GitHub (existing OAuth)
//   - Continue with Google (existing Supabase OAuth provider)
//
// All methods converge on the same Supabase session.
// Uses @ocnexus/brand tokens from design-system.css.

import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

type LoginView = 'options' | 'email-password' | 'magic-link' | 'password-reset' | 'magic-link-sent' | 'reset-sent' | 'signup-confirm';

export function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const {
    signInWithGitHub,
    signInWithGoogle,
    signInWithPassword,
    signUpWithPassword,
    signInWithMagicLink,
    requestPasswordReset,
    user,
    loading: authLoading,
  } = useAuth();

  const [view, setView] = useState<LoginView>('options');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);

  React.useEffect(() => {
    if (user && !authLoading) onAuthenticated();
  }, [user, authLoading, onAuthenticated]);

  const clearError = () => setError(null);

  // ── GitHub ───────────────────────────────────────────────────

  const handleGitHub = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithGitHub();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'GitHub sign-in failed');
      setLoading(false);
    }
  }, [signInWithGitHub]);

  const handleGoogle = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch {
      setError('Google sign-in failed. Please try again or use another sign-in option.');
      setLoading(false);
    }
  }, [signInWithGoogle]);

  // ── Email + password ─────────────────────────────────────────

  const handleEmailPassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (isSignUp) {
        const { needsConfirmation } = await signUpWithPassword(email, password);
        if (needsConfirmation) {
          setView('signup-confirm');
        } else {
          onAuthenticated();
        }
      } else {
        await signInWithPassword(email, password);
        onAuthenticated();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign-in failed';
      setError(normalizeAuthError(msg));
    } finally {
      setLoading(false);
    }
  }, [email, password, isSignUp, signInWithPassword, signUpWithPassword, onAuthenticated]);

  // ── Magic-link ───────────────────────────────────────────────

  const handleMagicLink = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInWithMagicLink(email);
      setView('magic-link-sent');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Magic link request failed';
      if (msg === 'captcha_failed') {
        setError('Please complete the captcha.');
      } else if (msg === 'service_unavailable') {
        setError('Sign-in service temporarily unavailable. Try GitHub or email/password.');
      } else {
        setError('Failed to send sign-in link. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [email, signInWithMagicLink]);

  // ── Password reset ───────────────────────────────────────────

  const handlePasswordReset = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setView('reset-sent');
    } catch {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, requestPasswordReset]);

  if (authLoading) {
    return (
      <LoginShell>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48 }}>
          <Spinner />
        </div>
      </LoginShell>
    );
  }

  return (
    <>
      <LoginBackground />
      <div className="login-layout">
        <FactoryDoor />

        <div className="login-center-col">
        <LoginCard>
          {view === 'magic-link-sent' && (
            <SentConfirmation
              title="Check your inbox"
              body={`We sent a sign-in link to ${email}. Click the link in the email to continue.`}
              note="The link expires in 15 minutes and can only be used once."
              onBack={() => { setView('magic-link'); clearError(); }}
            />
          )}

          {view === 'reset-sent' && (
            <SentConfirmation
              title="Check your inbox"
              body={`If ${email} has an account, we sent password reset instructions.`}
              note="The link expires in 15 minutes."
              onBack={() => { setView('options'); clearError(); }}
            />
          )}

          {view === 'signup-confirm' && (
            <SentConfirmation
              title="Confirm your email"
              body={`We sent a confirmation link to ${email}. Click it to complete your account.`}
              note="Check your spam folder if you don't see it."
              onBack={() => { setView('email-password'); clearError(); }}
            />
          )}

          {view === 'options' && (
            <OptionsView
              loading={loading}
              error={error}
              onGitHub={handleGitHub}
              onGoogle={() => { void handleGoogle(); }}
              onEmailPassword={() => { clearError(); setIsSignUp(false); setView('email-password'); }}
              onMagicLink={() => { clearError(); setView('magic-link'); }}
              onSignUp={() => { clearError(); setIsSignUp(true); setView('email-password'); }}
            />
          )}

          {view === 'email-password' && (
            <EmailPasswordView
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              isSignUp={isSignUp}
              setIsSignUp={setIsSignUp}
              loading={loading}
              error={error}
              onSubmit={handleEmailPassword}
              onBack={() => { clearError(); setView('options'); }}
              onForgotPassword={() => { clearError(); setView('password-reset'); }}
            />
          )}

          {view === 'magic-link' && (
            <MagicLinkView
              email={email}
              setEmail={setEmail}
              loading={loading}
              error={error}
              onSubmit={handleMagicLink}
              onBack={() => { clearError(); setView('options'); }}
            />
          )}

          {view === 'password-reset' && (
            <PasswordResetView
              email={email}
              setEmail={setEmail}
              loading={loading}
              error={error}
              onSubmit={handlePasswordReset}
              onBack={() => { clearError(); setView('email-password'); }}
            />
          )}
        </LoginCard>
        <BridgeLine />
        </div>

        <ForgeDoor />
      </div>
    </>
  );
}

// ─── Sub-views ────────────────────────────────────────────────────

function OptionsView({
  loading,
  error,
  onGitHub,
  onGoogle,
  onEmailPassword,
  onMagicLink,
  onSignUp,
}: {
  loading: boolean;
  error: string | null;
  onGitHub: () => void;
  onGoogle: () => void;
  onEmailPassword: () => void;
  onMagicLink: () => void;
  onSignUp: () => void;
}) {
  return (
    <>
      <LoginCardHeader
        title="Sign in or create an account"
        sub="New here? Create an account, or use Google, GitHub or an email link."
      />
      {error && <LoginError>{error}</LoginError>}
      <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* New user: email + password sign-up */}
        <AuthButton onClick={onSignUp} disabled={loading} variant="primary">
          <MailIcon />
          Create an account
        </AuthButton>
        {/* Returning user: email + password sign-in */}
        <AuthButton onClick={onEmailPassword} disabled={loading} variant="secondary">
          <MailIcon />
          Sign in with email and password
        </AuthButton>
        {/* Magic-link (works for new and returning users) */}
        <AuthButton onClick={onMagicLink} disabled={loading} variant="secondary">
          <MagicLinkIcon />
          Email me a sign-in link
        </AuthButton>
        <OrDivider />
        {/* GitHub */}
        <AuthButton onClick={onGitHub} disabled={loading} variant="github">
          {loading ? <Spinner size={16} /> : <GitHubIcon />}
          Continue with GitHub
        </AuthButton>
        <AuthButton onClick={onGoogle} disabled={loading} variant="secondary">
          {loading ? <Spinner size={16} /> : <GoogleIcon />}
          Continue with Google
        </AuthButton>
      </div>
      <LoginFooterLinks onSignUp={onSignUp} />
    </>
  );
}

function EmailPasswordView({
  email, setEmail, password, setPassword, isSignUp, setIsSignUp,
  loading, error, onSubmit, onBack, onForgotPassword,
}: {
  email: string; setEmail: (v: string) => void;
  password: string; setPassword: (v: string) => void;
  isSignUp: boolean; setIsSignUp: (v: boolean) => void;
  loading: boolean; error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  onForgotPassword: () => void;
}) {
  return (
    <>
      <LoginCardHeader
        title={isSignUp ? 'Create account' : 'Sign in'}
        sub={isSignUp ? 'Start shipping with OCNexus.' : 'Enter your email and password.'}
        onBack={onBack}
      />
      {error && <LoginError>{error}</LoginError>}
      <form onSubmit={onSubmit} style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField label="Email">
          <input
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoFocus
            style={inputStyle}
          />
        </FormField>
        <FormField label="Password">
          <input
            type="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            placeholder={isSignUp ? 'Min 12 chars with uppercase, number, symbol' : '••••••••••••'}
            required
            minLength={isSignUp ? 12 : 1}
            style={inputStyle}
          />
        </FormField>
        {!isSignUp && (
          <div style={{ textAlign: 'right', marginTop: -4 }}>
            <button type="button" onClick={onForgotPassword} style={linkBtnStyle}>Forgot password?</button>
          </div>
        )}
        <AuthButton type="submit" disabled={loading} variant="primary">
          {loading ? <Spinner size={16} /> : null}
          {isSignUp ? 'Create account' : 'Sign in'}
        </AuthButton>
      </form>
      <div style={{ textAlign: 'center', padding: '12px 24px 0', fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
        {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
        <button type="button" onClick={() => setIsSignUp(!isSignUp)} style={linkBtnStyle}>
          {isSignUp ? 'Sign in' : 'Sign up'}
        </button>
      </div>
      <LoginTerms />
    </>
  );
}

function MagicLinkView({
  email, setEmail, loading, error, onSubmit, onBack,
}: {
  email: string; setEmail: (v: string) => void;
  loading: boolean; error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}) {
  return (
    <>
      <LoginCardHeader title="Sign in with email" sub="We'll email you a sign-in link — no password needed." onBack={onBack} />
      {error && <LoginError>{error}</LoginError>}
      <form onSubmit={onSubmit} style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField label="Email">
          <input
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoFocus
            style={inputStyle}
          />
        </FormField>
        <AuthButton type="submit" disabled={loading} variant="primary">
          {loading ? <Spinner size={16} /> : <MagicLinkIcon />}
          Send sign-in link
        </AuthButton>
      </form>
      <LoginTerms />
    </>
  );
}

function PasswordResetView({
  email, setEmail, loading, error, onSubmit, onBack,
}: {
  email: string; setEmail: (v: string) => void;
  loading: boolean; error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}) {
  return (
    <>
      <LoginCardHeader title="Reset password" sub="Enter your email to receive a reset link." onBack={onBack} />
      {error && <LoginError>{error}</LoginError>}
      <form onSubmit={onSubmit} style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <FormField label="Email">
          <input
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoFocus
            style={inputStyle}
          />
        </FormField>
        <AuthButton type="submit" disabled={loading} variant="primary">
          {loading ? <Spinner size={16} /> : null}
          Send reset link
        </AuthButton>
      </form>
    </>
  );
}

function SentConfirmation({ title, body, note, onBack }: { title: string; body: string; note: string; onBack: () => void }) {
  return (
    <div style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', textAlign: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--green-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5"/>
        </svg>
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>{body}</div>
      <div style={{ fontSize: 12, color: 'var(--text-ghost)', fontFamily: 'var(--font-mono)' }}>{note}</div>
      <button type="button" onClick={onBack} style={linkBtnStyle}>← Go back</button>
    </div>
  );
}

// ─── Shared layout pieces ─────────────────────────────────────────

function LoginBackground() {
  return (
    <div className="login-bg">
      <div className="login-bg-grid" />
      <div className="login-bg-glow" />
      <div className="login-bg-glow-2" />
    </div>
  );
}

function LoginShell({ children }: { children: React.ReactNode }) {
  return <div className="login-layout"><div className="login-card">{children}</div></div>;
}

function FactoryDoor() {
  return (
    <div className="login-door login-door--factory">
      <div className="login-door-tag">The Factory — for the product you don't have yet</div>
      <h2 className="login-door-headline">Describe it. We ship it.</h2>
      <p className="login-door-copy">
        A complete SaaS — auth, billing, hosting — built and deployed on
        your own Stripe and your own data. Quoted before a line of code
        is written. Includes the Forge.
      </p>
      <div className="login-door-trace" aria-label="Illustrative Factory workflow">
        <span className="login-door-trace-step">Example workflow</span><br />
        <span className="login-door-trace-step">foundation</span><span className="login-door-trace-check">✓</span><br />
        <span className="login-door-trace-step">auth</span><span className="login-door-trace-check">✓</span><br />
        <span className="login-door-trace-step">billing</span><span className="login-door-trace-check">✓</span><br />
        <span className="login-door-trace-step">deploy</span><span className="login-door-trace-check">✓</span>
        <span className="login-door-trace-out">live on your domain — quoted up front</span>
      </div>
      <a href="https://ocnexus.dev/factory" className="login-door-cta">
        Build with the Factory
        <span className="login-door-cta-arrow" aria-hidden="true">→</span>
      </a>
    </div>
  );
}

function LoginCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="login-card">
      <div className="login-status-bar" />
      {children}
    </div>
  );
}

function LoginCardHeader({
  title,
  sub,
  onBack,
}: {
  title: string;
  sub: string;
  onBack?: () => void;
}) {
  return (
    <div className="login-card-header">
      <div className="login-logo-row">
        <div className="login-logo-mark">
          <svg viewBox="0 0 48 28" fill="none" xmlns="http://www.w3.org/2000/svg" width="36" height="20">
            <defs><linearGradient id="login-grad" x1="0" y1="14" x2="42" y2="14" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#00E5FF"/><stop offset="100%" stopColor="#8B5CF6"/></linearGradient></defs>
            <path d="M13 6C7.5 6 3 9.9 3 14s4.5 8 10 8c3.2 0 6-1.4 8-3.6C23 20.6 25.8 22 29 22c5.5 0 10-3.9 10-8s-4.5-8-10-8c-3.2 0-6 1.4-8 3.6C19 7.4 16.2 6 13 6z" stroke="url(#login-grad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <span className="login-logo-name">OCNexus</span>
        {onBack && (
          <button type="button" onClick={onBack} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)' }}>
            ← Back
          </button>
        )}
      </div>
      <div className="login-card-title">{title}</div>
      <div className="login-card-sub">{sub}</div>
    </div>
  );
}

function LoginError({ children }: { children: React.ReactNode }) {
  return (
    <div className="login-error" style={{ margin: '0 24px' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
      </svg>
      {children}
    </div>
  );
}

function OrDivider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-ghost)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      or
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{label}</label>
      {children}
    </div>
  );
}

// Sign-up stays inside the portal (same URL) so an in-flight MCP authorize
// request (authorization_request_id) is not lost by bouncing to the website.
function LoginFooterLinks({ onSignUp }: { onSignUp: () => void }) {
  return (
    <div className="login-card-footer">
      <span className="login-footer-text">
        New to OCNexus?{' '}
        <button
          type="button"
          onClick={onSignUp}
          className="login-footer-link"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
        >
          Create an account →
        </button>
      </span>
    </div>
  );
}

function LoginTerms() {
  return (
    <div style={{ padding: '12px 24px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6, fontFamily: 'var(--font-mono)' }}>
      By continuing, you agree to our{' '}
      <a href="https://ocnexus.dev/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-dim)', textDecoration: 'underline' }}>Terms</a>
      {' '}and{' '}
      <a href="https://ocnexus.dev/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-dim)', textDecoration: 'underline' }}>Privacy Policy</a>.
    </div>
  );
}

function ForgeDoor() {
  return (
    <div className="login-door login-door--forge">
      <div className="login-door-tag">The Forge — for the repo you already have</div>
      <h2 className="login-door-headline">Keep your code healthy.</h2>
      <p className="login-door-copy">
        From bug report to merged PR — we find it, fix it, and prove the
        fix landed. You stay the reviewer for as long as you want.
      </p>
      <div className="login-door-trace" aria-label="Illustrative Forge workflow">
        <span className="login-door-trace-step">Example workflow</span><br />
        <span className="login-door-trace-step">classify</span><span className="login-door-trace-check">✓</span><br />
        <span className="login-door-trace-step">spec</span><span className="login-door-trace-check">✓</span><br />
        <span className="login-door-trace-step">implement</span><span className="login-door-trace-check">✓</span><br />
        <span className="login-door-trace-step">review</span><span className="login-door-trace-check">✓</span><br />
        <span className="login-door-trace-step">ci</span><span className="login-door-trace-check">✓</span>
        <span className="login-door-trace-out">PR ready for checks and merge</span>
      </div>
      <a href="https://ocnexus.dev/forge" className="login-door-cta">
        Start with the Forge
        <span className="login-door-cta-arrow" aria-hidden="true">→</span>
      </a>
    </div>
  );
}

function BridgeLine() {
  return (
    <p className="login-bridge-line">
      Every Factory build graduates into the Forge when it goes live.
    </p>
  );
}

// ─── Button component ─────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'github' | 'google-stub';

function AuthButton({
  children,
  onClick,
  disabled,
  variant,
  type = 'button',
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant: ButtonVariant;
  type?: 'button' | 'submit';
  title?: string;
}) {
  const base: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '13px 18px',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'var(--font-mono)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'opacity 0.15s, transform 0.1s',
    opacity: disabled ? 0.55 : 1,
  };

  const styles: Record<ButtonVariant, React.CSSProperties> = {
    primary: { ...base, background: 'var(--orange)', color: '#0B0B1A' },
    secondary: { ...base, background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)' },
    github: { ...base, background: '#ffffff', color: '#24292f' },
    'google-stub': { ...base, background: 'var(--surface3)', color: 'var(--text-ghost)', cursor: 'not-allowed' },
  };

  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      style={styles[variant]}
    >
      {children}
    </button>
  );
}

// ─── Icons ────────────────────────────────────────────────────────

function GitHubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  );
}

function MagicLinkIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h6v6"/><path d="M10 14L21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    </svg>
  );
}

function Spinner({ size = 20 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        border: `2px solid rgba(0,0,0,0.15)`,
        borderTop: `2px solid currentColor`,
        borderRadius: '50%',
        animation: 'spin 0.75s linear infinite',
      }}
    />
  );
}

// ─── Styles ───────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: 7,
  color: 'var(--text)',
  fontSize: 14,
  fontFamily: 'var(--font-mono)',
  outline: 'none',
  transition: 'border-color 0.15s',
};

const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--orange)',
  fontSize: 13,
  fontFamily: 'var(--font-mono)',
  textDecoration: 'underline',
  padding: 0,
};

// ─── Helpers ─────────────────────────────────────────────────────

function normalizeAuthError(msg: string): string {
  if (msg.toLowerCase().includes('invalid login')) return 'Incorrect email or password.';
  if (msg.toLowerCase().includes('email not confirmed')) return 'Please confirm your email first — check your inbox.';
  if (msg.toLowerCase().includes('user already registered')) return 'An account with this email already exists. Try signing in.';
  if (msg.toLowerCase().includes('password')) return 'Password does not meet requirements.';
  return msg;
}

